import React, { useEffect } from 'react';
import { useGetMySubscriptionQuery, useCancelSubscriptionMutation } from '../../store/api/landingApi';
import { useSearchParams, Link } from 'react-router-dom';
import PageLoader from '../../components/PageLoader';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

const MySubscription = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user_id');
  const language = searchParams.get('language') || 'en';

  const { data, isLoading, isError, refetch } = useGetMySubscriptionQuery({
    user_id: userId,
    language: language
  });

  const [cancelSubscription] = useCancelSubscriptionMutation();

  const { package: userPackage, futureDate, language: lang } = data?.data || {};

  // Text Resources
  const texts = {
    ar: {
      your_subscription: "اشتراكك ",
      expire_on: "سيتم تجديد الاشتراك في",
      month: "شهر واحد",
      alert: "هل أنت متأكد أنك تريد إلغاء اشتراكك؟",
      cancel_subscription: "إلغاء الاشتراك",
      active: " مفعل",
      cancelled: "إلغاء",
      expired: "منتهي الصلاحية",
      renew: "تجديد",
      no_subscription: "انت غير مشترك في خدمات فسيلة",
      buy_subscription: " اشترك",
      proceed: " نعم",
      cancel: " الغاء",
      renew_alert: "انت مشترك الآن في خدمة فسيلة المميزة لايمكنك الاشتراك إلا بعد انتهاء الاشتراك الخاص بك"
    },
    en: {
      your_subscription: "Your Subscription",
      expire_on: "Next Renewal on",
      month: "", // Dynamic
      alert: "Are you sure you want to cancel your subscription?",
      cancel_subscription: "Cancel My Subscription",
      active: " Active",
      cancelled: "Cancelled",
      expired: " Expired",
      renew: "Renew Now",
      no_subscription: "You have't purchased any subscription yet",
      buy_subscription: " Buy Subscription",
      proceed: " Proceed",
      cancel: " Cancel",
      renew_alert: "You cant extend the subscription before it expires. Try to renew one day before expiry."
    }
  };

  const t = texts[language === 'ar' ? 'ar' : 'en'];

  const handleCancel = async () => {
    const result = await Swal.fire({
      title: t.alert,
      showCancelButton: true,
      cancelButtonColor: '#5E6278',
      confirmButtonColor: '#d33',
      confirmButtonText: t.proceed,
      cancelButtonText: t.cancel,
    });

    if (result.isConfirmed) {
      try {
        const response = await cancelSubscription({ user_id: userId, language }).unwrap();
        if (response.status) {
          toast.success(response.message);
          refetch();
        } else {
          toast.error(response.message);
        }
      } catch (error: any) {
        toast.error(error?.data?.message || 'Error cancelling subscription');
      }
    }
  };

  const handleRenewAlert = (e: React.MouseEvent) => {
    e.preventDefault();
    Swal.fire({
      title: t.renew_alert,
      showCancelButton: false,
      cancelButtonColor: '#5E6278',
      confirmButtonColor: '#5E6278',
      cancelButtonText: t.cancel,
    });
  };

  if (isLoading) return <PageLoader />;
  if (isError) return <div className="text-center p-5">Error loading subscription</div>;

  // Logic for display
  const isExpired = userPackage && new Date(userPackage.expired_at) < new Date();
  const isCancelled = userPackage?.is_cancelled == 1;
  const periodType = userPackage?.package?.period_type;
  const period = userPackage?.package?.period;
  
  let periodText = '';
  if (userPackage) {
      if (language === 'ar') {
         periodText = periodType === 'months' ? `${period} شهر` : `${period} يوم`;
      } else {
         periodText = periodType === 'months' ? `${period} Months Plan` : `${period} Days Plan`;
      }
  }

  const packageTitle = language === 'ar' ? userPackage?.package?.name_ar : userPackage?.package?.name;
  const expiryDate = userPackage ? new Date(userPackage.expired_at).toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }) : '';

  return (
    <div className="z-index-2 min-h-500px inner-box mb-15">
      <div className="container">
        <div className="row mt-15" data-aos="fade-up">
          <div className="col-md-4 offset-md-4 col-sm-12 col-xs-12">
            <h3 className={`m-5 ${language === 'ar' ? 'rtl' : ''}`}>{t.your_subscription}</h3>

            {userPackage ? (
              <>
                <div className={`item ${language === 'ar' ? 'rtl' : ''}`}>
                  <div className="package p-5 rounded-4 text-white mb-4" style={{ backgroundColor: '#36BD71', borderRadius: '20px', padding: '25px', height: 'auto', minHeight: '200px' }}>
                    <span className={`${language === 'ar' ? 'float-start' : 'float-end'} mt-4 fs-2`}>
                      {isExpired ? t.expired : (isCancelled ? t.cancelled : t.active)}
                    </span>
                    <h1 className="text-uppercase fw-bold text-white mb-2" style={{ fontSize: '31px', margin: '5px 0' }}>{packageTitle}</h1>
                    <p className="subtitle fs-5">{periodText}</p>
                  </div>
                </div>

                <div className="">
                   <h5 className={`m-5 ${language === 'ar' ? 'rtl' : ''}`}>
                     {isExpired ? t.expired : t.expire_on}: &nbsp;
                   </h5>
                   <h5 style={{ marginTop: '-30px' }} className={language === 'ar' ? '' : 'text-end'}>
                     {expiryDate}
                   </h5>
                </div>

                {isExpired ? (
                   <Link to={`/subscription?language=${language}&user_id=${userId}`} style={{ borderRadius: '30px', width: '90%' }} className="btn btn-base m-5 d-block text-center text-decoration-none bg-dark text-white py-3">
                     {t.renew}
                   </Link>
                ) : (
                   isCancelled && (
                     <a href="#" onClick={handleRenewAlert} style={{ borderRadius: '30px', width: '90%' }} className="btn btn-base m-5 d-block text-center text-decoration-none bg-dark text-white py-3">
                       {t.renew}
                     </a>
                   )
                )}

                {!isCancelled && !isExpired && (
                  <div className="text-center">
                    <button type="button" onClick={handleCancel} className="btn btn-dark m-5" style={{ borderRadius: '30px', width: '90%' }}>
                      {t.cancel_subscription}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <h4 className={`m-5 text-danger ${language === 'ar' ? 'rtl' : ''}`}>{t.no_subscription}</h4>
                <Link className="btn btn-base m-5 d-block text-center text-decoration-none bg-dark text-white py-3" style={{ borderRadius: '30px', width: '90%' }} to={`/subscription?language=${language}&user_id=${userId}`}>
                  {t.buy_subscription}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MySubscription;
